import { IconButton, Typography, styled } from '@mui/material';
import { PluginKey } from '@tiptap/pm/state';
import {
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import { memo, useRef } from 'react';
import {
  createUploadPlugin,
  startUpload,
} from '../../private/plugins/UploadPlugin';
import { ImageResizer } from './ImageResizer';
import { useImageState } from './imagesState';

import { imageExtension } from '@copera/tiptap/extensions';
import Iconify from 'src/components/iconify';
import Lightbox from 'src/components/lightbox';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    uploadImage: {
      uploadImage: (file: File, pos: number) => ReturnType;
    };
  }
}

const pluginKey = new PluginKey('uploadImage');

const Component = memo(
  ({ node, selected, updateAttributes, editor }: NodeViewProps) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const { content, loadContent, maximized, toggleMaximized } = useImageState(
      node.attrs.id,
    );

    if (!content?.loaded) {
      loadContent(node.attrs.id, editor.options.downloadFile);
    }

    const style = {
      width: node.attrs.width,
      height: node.attrs.height,
    };

    const ele =
      !content || !content?.loaded ? (
        <StyledLoadingImage style={style}>
          <Typography variant="overline" color="colorSecondary">
            Loading Image...
          </Typography>
        </StyledLoadingImage>
      ) : (
        <ImageWrapper>
          <StyledImage
            src={content.url}
            alt="attachment"
            ref={imgRef}
            draggable={false}
            style={style}
          />
          <ImageActions
            className="actions"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <IconButton onClick={() => toggleMaximized()}>
              <Iconify icon="tabler:arrows-maximize" />
            </IconButton>
          </ImageActions>
          {maximized && (
            <Lightbox
              slides={[
                {
                  src: content.url,
                  alt: node.attrs.alt,
                },
              ]}
              open
              disabledSlideshow
              disabledCaptions
              disabledFullscreen
              disabledThumbnails
              close={() => toggleMaximized()}
            />
          )}
        </ImageWrapper>
      );

    return (
      <>
        <NodeViewWrapper key={node.attrs.id}>{ele}</NodeViewWrapper>
        {imgRef.current && selected && (
          <ImageResizer
            target={imgRef.current}
            editor={editor}
            updateAttributes={updateAttributes}
          />
        )}
      </>
    );
  },
  (prev, next) =>
    prev.selected === next.selected &&
    prev.node.attrs.id === next.node.attrs.id,
);

const ImageWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  width: 'fit-content',
  '.actions': {
    opacity: 0,
    visibility: 'hidden',
    transition: theme.transitions.create(['opacity']),
  },
  '&:hover': {
    '.actions': {
      opacity: 1,
      visibility: 'visible',
    },
  },
}));

const ImageActions = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  display: 'flex',
  gap: theme.spacing(1),
}));

const StyledImage = styled('img')(({ theme }) => ({
  objectFit: 'contain',
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,

  '&:hover': {
    cursor: 'pointer',
    borderColor: theme.palette.primary.light,
    boxShadow: theme.shadows[3],
    transition: theme.transitions.create(['border-color', 'box-shadow']),
  },
}));

const StyledLoadingImage = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
}));

export const imageUploadExtension = imageExtension
  .extend({
    addCommandOption() {
      return [
        {
          title: 'Image',
          description: 'Upload an image.',
          searchTerms: ['upload', 'image'],
          icon: 'tabler:photo',
          command: ({ editor, range }) => {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = 'image/*';
            fileInput.onchange = () => {
              if (fileInput.files) {
                editor.commands.uploadImage(fileInput.files[0], range.from - 1);
              }
            };
            fileInput.click();
            editor.commands.deleteRange(range);
          },
        },
      ];
    },
    addProseMirrorPlugins() {
      return [
        createUploadPlugin({
          pluginKey,
          editor: this.editor,
          accept: (files: File[]) =>
            files.filter((file) => {
              if (!file.type.includes('image/')) {
                console.error('File type not supported.');
                return false;
              }
              if (file.size / 1024 / 1024 > 20) {
                console.error('File size too big (max 20MB).');
                return false;
              }
              return true;
            }),
        }),
      ];
    },
    addCommands() {
      return {
        uploadImage:
          (file: File, pos: number) =>
          ({ chain }) => {
            // check if the file is an image
            if (!file.type.includes('image/')) {
              console.error('File type not supported.');
              return chain().run();
            }

            // check if the file size is less than 20MB
            if (file.size / 1024 / 1024 > 20) {
              console.error('File size too big (max 20MB).');
              return chain().run();
            }

            startUpload(this.editor, pluginKey, file, pos);

            return chain().run();
          },
      };
    },
    addNodeView() {
      return ReactNodeViewRenderer(Component);
    },
  })
  .configure();
